import React, {useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import {Col, Divider, Form, Grid, Input, Row, Select, Typography} from 'antd'
import {getAmountWithCurrency, isEmptyOrSpaces} from '@/utils'
import {Description} from '@/components/dumb/Description'
import {TitleWithStatus} from '@/components/composite/TitleWithStatus'
import {ModalContainer} from '@/components/containers'
import {AmountFormField, ModalFormFooter} from '@/components'
import {RefundReasonEnum} from '@/api/transactions/models/RefundReasonEnum'

import {PaymentViewModalPoprs} from './props'
import styles from './styles.scss'

const {Title} = Typography
const { TextArea } = Input
const {useBreakpoint} = Grid

export const PaymentViewModal: React.FC<PaymentViewModalPoprs> = (
    { injectableIdentifier, selectedPayment, isLoading, isForRefund, statusProps, onCancel, onFinish }
) => {
    const { t } = useTranslation()
    const { t: tEnums } = useTranslation('enums')
    const screens = useBreakpoint()
    const isMobile = screens.xs
    const [form] = Form.useForm()
    const {amount, description, balance, currency} = selectedPayment
    const isRefundWithBalance = Boolean(balance) && isForRefund

    const refundReasonOptions = [
        { label: tEnums(`refundReason.${RefundReasonEnum.DUPLICATE}`), value: 'duplicate' },
        { label: tEnums(`refundReason.${RefundReasonEnum.FRAUDULENT}`), value: 'fraudulent' },
        { label: tEnums(`refundReason.${RefundReasonEnum.CUSTOMER_REQUEST}`), value: 'customer-request' },
        { label: tEnums(`refundReason.${RefundReasonEnum.OTHER}`), value: 'other' }
    ]

    useEffect(() => {
        form.setFieldsValue({
            amount,
            reason: null,
            description: null
        })
    }, [form, amount])

    const onModalCancel = () => {
        form.resetFields()
        onCancel()
    }

    const [modalTitle, bodyTitle, btnText] = (() => {
        if (isForRefund) {
            return [t('PaymentViewModal.paymentRefund'), t('PaymentViewModal.refundAmount'), t('PaymentViewModal.refund')]
        }
        return [t('PaymentViewModal.paymentCharge'), t('PaymentViewModal.chargeAmount'), t('PaymentViewModal.charge')]
    })()

    const renderForm = () => (
        <Form
            id={'paymentForm'}
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={onFinish}
            initialValues={{amount}}
        >
            <AmountFormField
                form={form}
                shouldAcceptZero={false}
                rules={[
                    {
                        validator: (field, value) => {
                            const amountOrBalance = isRefundWithBalance ? balance : amount

                            if (isEmptyOrSpaces(value) || parseFloat(value) <= amountOrBalance) {
                                return Promise.resolve()
                            }

                            const errorMessage = isForRefund
                                ? t('PaymentViewModal.errors.amountRefundExceed')
                                : t('PaymentViewModal.errors.amountChargeExceed')

                            const error = new Error(errorMessage)
                            return Promise.reject(error)
                        }
                    }
                ]}
                inputProps={{placeholder: t('PaymentViewModal.chargeAmount')}}
                currency={currency}
            />
            {isForRefund && (
                <>
                    <Form.Item
                        name={'reason'}
                        label={t('PaymentViewModal.refundReason.reason')}
                        rules={[
                            {
                                required: true,
                                message: t('PaymentViewModal.refundReason.reasonRequired')
                            }
                        ]}
                    >
                        <Select
                            options={refundReasonOptions}
                            placeholder={t('PaymentViewModal.refundReason.selectReason')}
                        />
                    </Form.Item>
                    <Form.Item
                        name={'description'}
                    >
                        <TextArea
                            placeholder={t('PaymentViewModal.refundReason.addDetails')}
                            maxLength={50}
                            showCount={true}
                        />
                    </Form.Item>
                </>
            )}
        </Form>
    )

    return (
        <ModalContainer
            title={modalTitle}
            injectableIdentifier={injectableIdentifier}
            onOk={onModalCancel}
            onCancel={onModalCancel}
            width={'700px'}
            footer={<ModalFormFooter
                okText={btnText}
                confirmLoading={isLoading}
                formId={'paymentForm'}
                onCancel={onModalCancel}
            />}
        >
            {statusProps ? (
                <TitleWithStatus
                    subtitle={description}
                    statusProps={statusProps}
                >
                    {getAmountWithCurrency(amount, currency)}
                </TitleWithStatus>
            ) : null}

            <Divider/>
            <Row>
                <Col className={classNames({[styles.chargeInput]: isMobile})} xs={24} md={11}>
                    <Title level={5}>{bodyTitle}</Title>
                    {renderForm()}
                </Col>

                <Col xs={24} md={{span: 11, offset: 2}}>
                    <Description
                        dataSource={[
                            {
                                key: t('PaymentViewModal.paymentAmount'),
                                label: t('PaymentViewModal.paymentAmount'),
                                value: getAmountWithCurrency(amount, currency)
                            },
                            ...(isRefundWithBalance ? [{
                                key: t('PaymentViewModal.balance'),
                                label: t('PaymentViewModal.balance'),
                                value: getAmountWithCurrency(balance, currency)
                            }] : [])
                        ]}
                    />
                </Col>
            </Row>

        </ModalContainer>
    )
}
